// The server chain then runs over the SAME unmodified bytes; its dedup meets these fields as
// existing org fields and resolves the duplicate candidates to them (silently, once overlap
// entries, which is noisy but never wrong: contents still place INTO the created fields).
import { api } from "../api";
import type { BoundaryCandidate } from "./boundaryScan";
import type { BoundaryOverlapHit, DashboardField } from "../types/contract";

export interface FastPathReport {
  created: { candidate: BoundaryCandidate; field: DashboardField }[];
  /* * matched an EXISTING field — not created; the name of what it matched is the user's answer */
  matchedExisting: {
    candidate: BoundaryCandidate;
    hits: BoundaryOverlapHit[];
  }[];
  failed: { candidate: BoundaryCandidate; error: string }[];
}

const OVERLAP_FLOOR_PCT = 1;
/* * the chain's 200-fields-per-drop product cap — the fast path never exceeds what the chain allows */
export const MAX_FAST_CREATES = 200;

export async function createFieldsFast(
  candidates: BoundaryCandidate[],
  opts: {
    onProgress?: (done: number, total: number, last?: string) => void;
    /*
     *
     * fires ONLY on an actual create — the dashboard invalidation hook (review: skips/failures.
     */
    onCreated?: (field: DashboardField) => void;
  } = {},
): Promise<FastPathReport> {
  const report: FastPathReport = {
    created: [],
    matchedExisting: [],
    failed: [],
  };
  let done = 0;
  for (const candidate of candidates.slice(0, MAX_FAST_CREATES)) {
    try {
      const hits = await api.checkBoundaryOverlap({
        geojson: candidate.geojson,
        min_overlap_pct: OVERLAP_FLOOR_PCT,
      });
      if (hits.length) {
        report.matchedExisting.push({ candidate, hits });
      } else {
        const field = await api.createFieldFromBoundary(
          candidate.name,
          candidate.geojson,
        );
        report.created.push({ candidate, field });
        // PARITY WITH ADD-FIELD (review, breaks-ui class): every other create path files the
        // covariate build after a synchronous boundary write — without this, a fast-pathed field
        // never builds and its map stays empty forever (the chain only builds fields IT creates).
        // Fire-and-forget: the build queue's own admission gates own the pacing.
        void api.buildCovariates(field.field_id).catch(() => {
          /* the flags column will say so */
        });
        opts.onCreated?.(field);
      }
    } catch (e) {
      // a failed create falls back to the server chain creating it — slower, never lost
      report.failed.push({
        candidate,
        error: e instanceof Error ? e.message : "create failed",
      });
    }
    done++;
    opts.onProgress?.(done, candidates.length, candidate.name);
  }
  return report;
}
